import { useDispatch } from 'react-redux';
import { useCallback, useEffect } from 'react';
import { DatePicker, Form, Input, Radio } from 'antd';
import { Link, useLoaderData, useNavigate } from 'react-router-dom';
import { AxiosResponse } from 'axios';
import { GenderEnum } from '../../../../../enums/gender.enum';
import { GetUserSuccessResponse } from '../../../../../types/api/get-user-success-response';
import dayjs from 'dayjs';
import { RoleEnum } from '../../../../../enums/role.enum';
import { showLoadingAlertMessage } from '../../../../../../../utils/show-loading-alert-message/show-loading-alert-message';
import { networkCall } from '../../../../../../../utils/network-call/network-call';
import { showDefaultErrorMessages } from '../../../../../../../utils/show-default-error-message/show-default-error-messages';
import { AlertMessageState, clearAlertMessage, showAlertMessage } from '../../../../../../../redux/reducer/alert-message.reducer';
import { AlertMessages } from '../../../../../../../enums/alert-messages.enum';
import { DefaultErrorResponse } from '../../../../../../../types/api/error/default-error-response/default-error-respose.type';
import { ValidationErrorResponseData } from '../../../../../../../types/api/error/validation-error-response-data/validation-error-response-data.type.';
import { tranformAPIValidationErrorToAntDValidationMessage } from '../../../../../../../utils/validation/transform-api-validation-errors-to-antd-validation-messsages';
import { setSelectedMenuItemKeys } from '../../../../../../../redux/reducer/antd-layout-info.reducer';
import { SelectOption } from '../../../../../../../types/select-option';
import { getImageUrl } from '../../../../../../../utils/image-helper/get-image-url';
import { CMSButton } from '../../../../../../../components/cms/button/cms-button.';

export const AdminUserEdit = () => {
    const dispatch = useDispatch();
    const [form] = Form.useForm();
    const navigate = useNavigate();
    const loaderData: any = useLoaderData();

    const getRoleName = useCallback((role: RoleEnum) => {
        switch (role) {
            case RoleEnum.Admin:
                return 'admin';
            case RoleEnum.CompanyAdmin:
                return 'cégvezető';
            case RoleEnum.Maintainer:
                return 'karbantartási vezető';
            case RoleEnum.User:
                return 'felhasználó';
            default:
        }
    }, []);

    const handleFinish = async (values: any) => {
        showLoadingAlertMessage();

        let employmentDate;
        if (values.employmentDate)
            employmentDate = `${ values.employmentDate.get('year') }-${ values.employmentDate.get('month') + 1 }-${ values.employmentDate.get('date') }`;

        const response = await networkCall(`/users/${ loaderData.getUserResponse?.data?.user?.id }`, {
            method: 'PUT',
            body: {
                email: values.email,
                name: values.name,
                gender: values.gender,
                employmentDate
            }
        });

        const messageShowed = showDefaultErrorMessages(response);
        if (messageShowed) return;

        dispatch(clearAlertMessage());
        const alertMessageState: AlertMessageState = {
            alertMessageType: 'error',
            alertMessageText: AlertMessages.UnknownErrorTryAgain
        };

        if (response?.status === 200) {
            alertMessageState.alertMessageText = 'Mentés sikeres.';
            alertMessageState.alertMessageType = 'success';
        } else if (response?.status === 404) {
            const res: AxiosResponse<DefaultErrorResponse> = response;

            switch (res.data.code) {
                case 7001:
                    alertMessageState.alertMessageText = 'A felhasználó nem található.'
                    break;
            }
        } else if (response?.status === 400) {
            const res: AxiosResponse<DefaultErrorResponse> = response;

            switch (res.data.code) {

                case 1000:
                    const _res: AxiosResponse<DefaultErrorResponse<ValidationErrorResponseData[]>> = response;
                    alertMessageState.alertMessageText = 'Hibásan megadott beviteli mezők.';
                    const validationErrors = tranformAPIValidationErrorToAntDValidationMessage(_res.data.data!);
                    form.setFields(validationErrors);
                    break;

            }

        }

        dispatch(showAlertMessage(alertMessageState));
    }

    useEffect(() => {
        dispatch(setSelectedMenuItemKeys(['admin-users']));
    }, [dispatch]);

    useEffect(() => {
        if(loaderData.getUserResponse?.status === 200) {
            const res: AxiosResponse<GetUserSuccessResponse> = loaderData.getUserResponse;
            form.setFieldValue('name', res.data.user.name);
            form.setFieldValue('email', res.data.user.email);
            form.setFieldValue('gender', res.data.user.gender);
            form.setFieldValue('role', getRoleName(res.data.user.role));
            form.setFieldValue('uniqueId', res.data.user.uniqueId);
            form.setFieldValue('employmentDate', res.data.user.employmentDate ? dayjs(res.data.user.employmentDate * 1000) : null);
        } else {
            dispatch(clearAlertMessage());
            dispatch(showAlertMessage({
                alertMessageText: 'Hiba a felhasználó lekérdezése közben.',
                alertMessageType: 'error'
            }));
            navigate('/cms/admin/users');
        }
    }, [loaderData.getUserResponse, dispatch, navigate, form, getRoleName]);

    const genderOptions: SelectOption[] = [
        { label: 'nő', value: GenderEnum.female },
        { label: 'férfi', value: GenderEnum.male }
    ];

    return (
        <>
            <div className="page-title">Felhasználó módosítása</div>
            <Form
                form={form}
                onFinish={handleFinish}
                layout='vertical'
            >
    
                <Form.Item
                    label={<b>Név</b>}
                    name='name'
                    rules={[{required: true, message: 'Név megadása kötelező!'}]}
                >
                    <Input className='box-shadow-4-4-8-0 border-0' />
                </Form.Item>

                <Form.Item
                    label={<b>Email cím</b>}
                    name='email'
                    rules={[{required: true, message: 'Email cím megadása kötelező!'}, {type: 'email', message: 'Helytelen email formátum!'}]}
                >
                    <Input className='box-shadow-4-4-8-0 border-0' />
                </Form.Item>

                {
                    loaderData.getUserResponse?.data?.user?.role === RoleEnum.User && (
                        <Form.Item
                            label={<b>Egyedi azonosító</b>}
                            name='uniqueId'
                        >
                            <Input className='box-shadow-4-4-8-0 border-0 fw-bold' disabled />
                        </Form.Item>
                    )
                }

                <Form.Item
                    label={<b>Jogosultsági szint</b>}
                    name='role'
                >
                    <Input className='box-shadow-4-4-8-0 border-0 fw-bold' disabled />
                </Form.Item>

                <Form.Item
                    label={<b>Neme</b>}
                    name='gender'
                >
                    <Radio.Group className='custom-radio-group-input' optionType='button' options={genderOptions} buttonStyle='solid' />
                </Form.Item>

                <Form.Item
                    name="employmentDate"
                    label={<b>Munkába állás ideje</b>}
                >
                    <DatePicker className='box-shadow-4-4-8-0' style={{ width: '100%' }} />
                </Form.Item>

                <Form.Item className='text-end' style={{ marginTop: '35px' }}>
                    <Link to='/cms/admin/users'>
                        <CMSButton text='Vissza' type='white' iconSrc={getImageUrl('back-icon.png')} />
                    </Link>
                    &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
                    <CMSButton text='Mentés' iconSrc={getImageUrl('Save-icon.png')} htmlType='submit' />
                </Form.Item>
                        
            </Form>
        </>
    );
};